import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "@/hooks/use-auth";
import { AuthSession, User } from "@/types/auth";
import { AuthenticationFailedError } from "./AuthError";
import {
  buildSigninUrl,
  clearStoredSession,
  getStoredSession,
  refreshAuth,
  startAuth,
  storeSession,
  verifyAuth,
} from "./auth";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user] = useState<User | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const refreshPromiseRef = useRef<Promise<string> | null>(null);

  const applySession = useCallback((nextSession: AuthSession | null) => {
    sessionRef.current = nextSession;
    setSession(nextSession);
  }, []);

  useEffect(() => {
    applySession(getStoredSession());
    setIsLoading(false);
  }, [applySession]);

  const persistSession = useCallback(
    (nextSession: AuthSession) => {
      storeSession(nextSession);
      applySession(nextSession);
    },
    [applySession]
  );

  const getAccessToken = useCallback(async (): Promise<string> => {
    const currentSession = sessionRef.current ?? getStoredSession();

    if (!currentSession) {
      clearStoredSession();
      applySession(null);
      throw new AuthenticationFailedError("No active authentication session");
    }

    if (sessionRef.current !== currentSession) {
      applySession(currentSession);
    }

    return currentSession.accessToken;
  }, [applySession]);

  const clearSession = useCallback(() => {
    refreshPromiseRef.current = null;
    clearStoredSession();
    applySession(null);
  }, [applySession]);

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      const currentSession = sessionRef.current ?? getStoredSession();

      if (!currentSession?.refreshToken) {
        clearSession();
        throw new AuthenticationFailedError("No refresh token available");
      }

      try {
        const refreshedSession = await refreshAuth({
          refreshToken: currentSession.refreshToken,
        });
        persistSession(refreshedSession);
        return refreshedSession.accessToken;
      } catch (error) {
        clearSession();
        if (error instanceof AuthenticationFailedError) {
          throw error;
        }
        throw new AuthenticationFailedError("Unable to refresh authentication");
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [clearSession, persistSession]);

  const startLogin = useCallback(async (email: string, organizationId?: string) => {
    clearSession();
    await startAuth({
      email,
      role: "user",
      organizationId,
    });
  }, [clearSession]);

  const verifyOtpCode = useCallback(async (email: string, otpCode: string) => {
    const verifiedSession = await verifyAuth({
      email,
      otp_code: otpCode,
      role: "user",
    });

    persistSession(verifiedSession);
  }, [persistSession]);

  const acceptSession = useCallback((nextSession: AuthSession) => {
    persistSession(nextSession);
  }, [persistSession]);

  const logout = useCallback(() => {
    clearSession();

    if (!window.location.pathname.startsWith("/signin")) {
      window.location.assign(buildSigninUrl());
    }
  }, [clearSession]);

  const authValue = useMemo(
    () => ({
      isAuthenticated: !!session?.accessToken && !!session?.refreshToken,
      isLoading,
      startLogin,
      verifyOtp: verifyOtpCode,
      acceptSession,
      clearSession,
      logout,
      user,
      getAccessToken,
      refreshAccessToken,
    }),
    [
      acceptSession,
      clearSession,
      getAccessToken,
      isLoading,
      logout,
      refreshAccessToken,
      session,
      startLogin,
      user,
      verifyOtpCode,
    ]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
}
